/**
 * Flamegraph Diff Utilities
 *
 * Functions for comparing two profile trees and computing differences
 * for regression detection and performance analysis.
 */

import { ProfileNode } from './flamegraphLayout.ts';

export interface DiffNode {
    baselineValue: number;
    comparisonValue: number;
    delta: number;  // Positive = regression (slower), Negative = improvement (faster)
    percentChange: number;
    diffType: 'increased' | 'decreased' | 'unchanged' | 'new' | 'removed';
}

export interface DiffResult {
    diffNodes: Map<string, DiffNode>;  // name -> diff info
    totalDelta: number;
    totalPercentChange: number;
    newNodes: string[];  // names of new nodes
    removedNodes: string[];  // names of removed nodes
}

/**
 * Threshold for considering a change as "unchanged" (5%)
 */
const UNCHANGED_THRESHOLD = 5.0;

/**
 * Clear diffData from all nodes in the tree
 * MUST be called before computing a new diff to avoid stale data
 */
function clearDiffData(node: ProfileNode | null): void {
    if (!node) return;

    // Clear diff data from this node
    delete node.diffData;

    // Recursively clear children
    if (node.children) {
        for (const child of node.children) {
            clearDiffData(child);
        }
    }
}

/**
 * Compute diff between baseline and comparison profiles using parallel tree walk
 */
export function computeDiff(
    baseline: ProfileNode,
    comparison: ProfileNode
): DiffResult {
    const diffNodes = new Map<string, DiffNode>();
    const newNodes: string[] = [];
    const removedNodes: string[] = [];

    // CRITICAL: Clear any existing diffData from previous diff calculations
    clearDiffData(baseline);
    clearDiffData(comparison);

    /**
     * Recursively walk both trees in parallel, matching children by name
     */
    function walkParallel(
        baselineNode: ProfileNode | null,
        comparisonNode: ProfileNode | null,
        name: string
    ) {
        // Both nodes exist - calculate diff
        if (baselineNode && comparisonNode) {
            const baselineValue = baselineNode.value;
            const comparisonValue = comparisonNode.value;
            const delta = comparisonValue - baselineValue;
            const percentChange = baselineValue > 0
                ? (delta / baselineValue) * 100
                : (comparisonValue > 0 ? 100 : 0);

            let diffType: DiffNode['diffType'];
            if (Math.abs(percentChange) < UNCHANGED_THRESHOLD) {
                diffType = 'unchanged';
            } else if (delta > 0) {
                diffType = 'increased';
            } else {
                diffType = 'decreased';
            }

            // Store diff data in BOTH nodes (we display either baseline or comparison tree)
            const diffNodeData = {
                baselineValue,
                comparisonValue,
                delta,
                percentChange,
                diffType
            };

            baselineNode.diffData = diffNodeData;
            comparisonNode.diffData = diffNodeData;

            // ALSO store in Map for backwards compatibility / summary stats
            diffNodes.set(name, diffNodeData);

            // Walk children - match by name
            const baselineChildren = baselineNode.children || [];
            const comparisonChildren = comparisonNode.children || [];

            // Build map of comparison children by name for quick lookup
            const comparisonChildMap = new Map<string, ProfileNode>();
            comparisonChildren.forEach(child => {
                comparisonChildMap.set(child.name, child);
            });

            // Build set of all child names
            const allChildNames = new Set<string>();
            baselineChildren.forEach(child => allChildNames.add(child.name));
            comparisonChildren.forEach(child => allChildNames.add(child.name));

            // Process all children
            for (const childName of allChildNames) {
                const baselineChild = baselineChildren.find(c => c.name === childName);
                const comparisonChild = comparisonChildMap.get(childName);
                walkParallel(baselineChild || null, comparisonChild || null, childName);
            }
        }
        // Only comparison node exists - new node
        else if (comparisonNode) {
            newNodes.push(name);

            // Store diff data in new node
            comparisonNode.diffData = {
                baselineValue: 0,
                comparisonValue: comparisonNode.value,
                delta: comparisonNode.value,
                percentChange: 100,
                diffType: 'new'
            };

            diffNodes.set(name, comparisonNode.diffData);

            // Walk comparison children as all new
            if (comparisonNode.children) {
                for (const child of comparisonNode.children) {
                    walkParallel(null, child, child.name);
                }
            }
        }
        // Only baseline node exists - removed node
        else if (baselineNode) {
            removedNodes.push(name);
            diffNodes.set(name, {
                baselineValue: baselineNode.value,
                comparisonValue: 0,
                delta: -baselineNode.value,
                percentChange: -100,
                diffType: 'removed'
            });

            // Walk baseline children as all removed
            if (baselineNode.children) {
                for (const child of baselineNode.children) {
                    walkParallel(child, null, child.name);
                }
            }
        }
    }

    // Start parallel walk from roots
    walkParallel(baseline, comparison, baseline.name || comparison.name || 'root');

    // Calculate total delta
    const totalDelta = comparison.value - baseline.value;
    const totalPercentChange = baseline.value > 0
        ? (totalDelta / baseline.value) * 100
        : 0;

    return {
        diffNodes,
        totalDelta,
        totalPercentChange,
        newNodes,
        removedNodes
    };
}

/**
 * Create a merged tree that combines both baseline and comparison profiles
 * Shows all nodes: new, removed, changed, and unchanged
 *
 * Strategy:
 * - Use comparison tree as primary structure (includes new nodes)
 * - Insert removed nodes in their appropriate positions
 * - All nodes get diffData attached during merge
 */
export function createMergedDiffTree(
    baseline: ProfileNode,
    comparison: ProfileNode,
    diffResult: DiffResult
): ProfileNode {
    let nextId = 0;

    /**
     * Recursively merge nodes from both trees
     */
    function mergeNodes(
        baselineNode: ProfileNode | null,
        comparisonNode: ProfileNode | null,
        parentId: number
    ): ProfileNode {
        // Determine node name and value
        const name = (comparisonNode?.name || baselineNode?.name)!;
        let value: number;
        let diffData: DiffNode | undefined;

        if (baselineNode && comparisonNode) {
            // Node exists in both - use comparison value, diffData already exists
            value = comparisonNode.value;
            diffData = comparisonNode.diffData;
        } else if (comparisonNode) {
            // New node - use comparison value
            value = comparisonNode.value;
            diffData = comparisonNode.diffData || {
                baselineValue: 0,
                comparisonValue: comparisonNode.value,
                delta: comparisonNode.value,
                percentChange: 100,
                diffType: 'new'
            };
        } else if (baselineNode) {
            // Removed node - use baseline value (show what was removed)
            value = baselineNode.value;
            diffData = {
                baselineValue: baselineNode.value,
                comparisonValue: 0,
                delta: -baselineNode.value,
                percentChange: -100,
                diffType: 'removed'
            };
        } else {
            throw new Error('mergeNodes called with both nodes null');
        }

        // Create merged node
        const id = nextId++;
        const mergedNode: ProfileNode = {
            id,
            parentId,
            name,
            value,
            exclusive: 0,  // Will be calculated
            children: [],
            diffData
        };

        // Merge children from both trees
        const baselineChildren = baselineNode?.children || [];
        const comparisonChildren = comparisonNode?.children || [];

        // Build maps for quick lookup
        const baselineChildMap = new Map<string, ProfileNode>();
        baselineChildren.forEach(child => baselineChildMap.set(child.name, child));

        const comparisonChildMap = new Map<string, ProfileNode>();
        comparisonChildren.forEach(child => comparisonChildMap.set(child.name, child));

        // Get union of all child names
        const allChildNames = new Set<string>();
        baselineChildren.forEach(c => allChildNames.add(c.name));
        comparisonChildren.forEach(c => allChildNames.add(c.name));

        // Recursively merge all children
        let childValueSum = 0;
        for (const childName of allChildNames) {
            const baselineChild = baselineChildMap.get(childName) || null;
            const comparisonChild = comparisonChildMap.get(childName) || null;

            const mergedChild = mergeNodes(baselineChild, comparisonChild, id);
            mergedNode.children!.push(mergedChild);
            childValueSum += mergedChild.value;
        }

        // Calculate exclusive time
        mergedNode.exclusive = Math.max(0, value - childValueSum);

        return mergedNode;
    }

    // Start merge from roots
    return mergeNodes(baseline, comparison, -1);
}
